import { supabase } from "../../lib/supabase";
import { ensureV2ReminderIdentity } from "./v2ReminderRepository";

export const PUSH_SERVICE_WORKER_PATH = "/push-service-worker.js";
export const PUSH_STATUS = Object.freeze({
  supported: "supported",
  unsupported: "unsupported",
  unavailable: "unavailable",
  active: "active",
  inactive: "inactive",
  denied: "denied",
});

export function detectPushSupport(scope = globalThis) {
  const nav = scope?.navigator;
  const win = scope?.window || scope;
  const notificationSupported = Boolean(win?.Notification);
  const serviceWorkerSupported = Boolean(nav?.serviceWorker);
  const pushManagerSupported = Boolean(win?.PushManager);
  const supported = notificationSupported && serviceWorkerSupported && pushManagerSupported;

  return {
    supported,
    notificationSupported,
    serviceWorkerSupported,
    pushManagerSupported,
    permission: notificationSupported ? win.Notification.permission : "unsupported",
    platform: detectPlatform(nav),
    requiresIosInstall: isIos(nav) && !isStandalone(win, nav),
  };
}

export function getPermissionStatus(scope = globalThis) {
  return detectPushSupport(scope).permission;
}

export async function fetchVapidPublicKey(fetchImpl = fetch) {
  const response = await fetchImpl("/.netlify/functions/push-config", {
    headers: { Accept: "application/json" },
  });
  const data = await response.json().catch(() => ({}));

  if (!response.ok || !data?.ok || !data.publicKey) {
    return { ok: false, publicKey: "", error: data?.error || "Push configuration is unavailable." };
  }

  return { ok: true, publicKey: data.publicKey, error: "" };
}

export function urlBase64ToUint8Array(base64String = "") {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = `${base64String}${padding}`.replace(/-/g, "+").replace(/_/g, "/");
  const rawData = globalThis.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let index = 0; index < rawData.length; index += 1) {
    outputArray[index] = rawData.charCodeAt(index);
  }

  return outputArray;
}

export async function registerPushServiceWorker(navigatorRef = navigator) {
  if (!navigatorRef?.serviceWorker?.register) {
    return { ok: false, registration: null, error: "Service workers are not supported." };
  }

  try {
    const registration = await navigatorRef.serviceWorker.register(PUSH_SERVICE_WORKER_PATH, { scope: "/" });
    return { ok: true, registration, error: "" };
  } catch (error) {
    return { ok: false, registration: null, error: error?.message || "Service worker registration failed." };
  }
}

export async function getReadyServiceWorkerRegistration(navigatorRef = navigator) {
  if (!navigatorRef?.serviceWorker?.ready) {
    return { ok: false, registration: null, error: "Service workers are not supported." };
  }

  try {
    const registration = await navigatorRef.serviceWorker.ready;
    return { ok: true, registration, error: "" };
  } catch (error) {
    return { ok: false, registration: null, error: error?.message || "Service worker is not ready." };
  }
}

export async function getBrowserPushSubscription({ navigatorRef = navigator } = {}) {
  const ready = await getReadyServiceWorkerRegistration(navigatorRef);
  if (!ready.ok) return { ok: false, subscription: null, error: ready.error };

  const subscription = await ready.registration.pushManager.getSubscription();
  return { ok: true, subscription, error: "" };
}

export function serializePushSubscription(subscription) {
  const json = subscription?.toJSON?.() || {};
  return {
    endpoint: String(subscription?.endpoint || json.endpoint || ""),
    p256dh: String(json.keys?.p256dh || ""),
    auth: String(json.keys?.auth || ""),
  };
}

export async function upsertPushSubscription(subscription, { client = supabase, userAgent, platform } = {}) {
  const identity = await ensureV2ReminderIdentity(client);
  if (!identity.ok) return identity;

  const serialized = serializePushSubscription(subscription);
  if (!serialized.endpoint || !serialized.p256dh || !serialized.auth) {
    return { ok: false, user: identity.user, error: "Push subscription is incomplete." };
  }

  const row = {
    user_id: identity.user.id,
    endpoint: serialized.endpoint,
    p256dh: serialized.p256dh,
    auth: serialized.auth,
    user_agent: userAgent || getUserAgent(),
    platform: platform || detectPlatform(navigator),
    active: true,
    last_seen_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  const result = await client.from("v2_push_subscriptions").upsert(row, { onConflict: "endpoint" }).select().single();
  if (result.error) {
    return { ok: false, user: identity.user, error: result.error.message || "Push subscription upload failed." };
  }

  return { ok: true, user: identity.user, subscription: result.data, error: "" };
}

export async function enablePushNotifications({ client = supabase, fetchImpl = fetch, navigatorRef = navigator } = {}) {
  const support = detectPushSupport({ navigator: navigatorRef, window });
  if (!support.supported) {
    return { ok: false, status: getUnavailableStatus(support), support, error: "Push notifications are not supported here." };
  }

  if (support.permission === "denied") {
    return { ok: false, status: PUSH_STATUS.denied, support, error: "Notification permission is denied." };
  }

  const registrationResult = await registerPushServiceWorker(navigatorRef);
  if (!registrationResult.ok) {
    return { ok: false, status: PUSH_STATUS.unavailable, support, error: registrationResult.error };
  }

  const permission = support.permission === "granted" ? "granted" : await window.Notification.requestPermission();
  if (permission !== "granted") {
    return { ok: false, status: permission, support: { ...support, permission }, error: "Notification permission was not granted." };
  }

  const keyResult = await fetchVapidPublicKey(fetchImpl);
  if (!keyResult.ok) {
    return { ok: false, status: PUSH_STATUS.unavailable, support: { ...support, permission }, error: keyResult.error };
  }

  const ready = await getReadyServiceWorkerRegistration(navigatorRef);
  if (!ready.ok) {
    return { ok: false, status: PUSH_STATUS.unavailable, support: { ...support, permission }, error: ready.error };
  }

  const existing = await ready.registration.pushManager.getSubscription();
  const subscription =
    existing ||
    (await ready.registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(keyResult.publicKey),
    }));

  const uploaded = await upsertPushSubscription(subscription, {
    client,
    userAgent: getUserAgent(),
    platform: support.platform,
  });
  if (!uploaded.ok) {
    return { ok: false, status: PUSH_STATUS.unavailable, support: { ...support, permission }, error: uploaded.error };
  }

  return { ok: true, status: PUSH_STATUS.active, support: { ...support, permission }, subscription: uploaded.subscription, error: "" };
}

export async function reconcilePushSubscription({ client = supabase, navigatorRef = navigator } = {}) {
  const support = detectPushSupport({ navigator: navigatorRef, window });
  if (!support.supported) return { ok: false, status: getUnavailableStatus(support), support, error: "" };

  const ready = await getReadyServiceWorkerRegistration(navigatorRef);
  if (!ready.ok) return { ok: false, status: PUSH_STATUS.unavailable, support, error: ready.error };

  const subscription = await ready.registration.pushManager.getSubscription();
  if (!subscription) return { ok: true, status: PUSH_STATUS.inactive, support, subscription: null, error: "" };

  const uploaded = await upsertPushSubscription(subscription, {
    client,
    userAgent: getUserAgent(),
    platform: support.platform,
  });
  if (!uploaded.ok) {
    return { ok: false, status: PUSH_STATUS.unavailable, support, error: uploaded.error };
  }

  return { ok: true, status: PUSH_STATUS.active, support, subscription: uploaded.subscription, error: "" };
}

export async function disablePushNotifications({ client = supabase, navigatorRef = navigator } = {}) {
  const existing = await getBrowserPushSubscription({ navigatorRef });
  if (!existing.ok) return existing;

  const serialized = serializePushSubscription(existing.subscription);
  if (existing.subscription?.unsubscribe) {
    await existing.subscription.unsubscribe();
  }

  if (serialized.endpoint) {
    const identity = await ensureV2ReminderIdentity(client);
    if (!identity.ok) return identity;
    const result = await client
      .from("v2_push_subscriptions")
      .update({ active: false, updated_at: new Date().toISOString() })
      .eq("user_id", identity.user.id)
      .eq("endpoint", serialized.endpoint);
    if (result.error) {
      return { ok: false, user: identity.user, error: result.error.message || "Push subscription could not be disabled." };
    }
  }

  return { ok: true, status: PUSH_STATUS.inactive, error: "" };
}

export async function deactivateCurrentDevicePushSubscription({
  client = supabase,
  navigatorRef = navigator,
  userId = "",
  unsubscribeBrowser = true,
} = {}) {
  const existing = await getBrowserPushSubscription({ navigatorRef });
  if (!existing.ok) {
    return { ok: true, status: PUSH_STATUS.inactive, skipped: true, error: "" };
  }

  const serialized = serializePushSubscription(existing.subscription);
  if (unsubscribeBrowser && existing.subscription?.unsubscribe) {
    const unsubscribed = await existing.subscription.unsubscribe();
    if (unsubscribed === false) {
      return { ok: false, error: "This device could not unsubscribe from Push notifications." };
    }
  }

  if (serialized.endpoint && userId) {
    const result = await client
      .from("v2_push_subscriptions")
      .update({ active: false, updated_at: new Date().toISOString() })
      .eq("user_id", userId)
      .eq("endpoint", serialized.endpoint);
    if (result.error) {
      return { ok: false, error: "This device Push subscription could not be deactivated." };
    }
  }

  return { ok: true, status: PUSH_STATUS.inactive, error: "" };
}

export async function sendTestPushNotification({ client = supabase, fetchImpl = fetch } = {}) {
  const identity = await ensureV2ReminderIdentity(client);
  if (!identity.ok) return identity;
  const session = await client.auth.getSession();
  const accessToken = session.data?.session?.access_token;
  if (!accessToken) return { ok: false, error: "Supabase access token is unavailable." };

  const response = await fetchImpl("/.netlify/functions/send-test-push", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({}),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data?.ok) {
    return { ok: false, error: data?.error || "Test push failed.", ...data };
  }
  return data;
}

function getUnavailableStatus(support) {
  if (support.permission === "denied") return PUSH_STATUS.denied;
  return PUSH_STATUS.unsupported;
}

function detectPlatform(nav = navigator) {
  const uaDataPlatform = nav?.userAgentData?.platform;
  const platform = uaDataPlatform || nav?.platform || "";
  const userAgent = nav?.userAgent || "";
  if (/iPad|iPhone|iPod/i.test(platform) || /iPad|iPhone|iPod/i.test(userAgent)) return "iOS";
  if (/Android/i.test(platform) || /Android/i.test(userAgent)) return "Android";
  if (/Win/i.test(platform)) return "Windows";
  if (/Mac/i.test(platform)) return "macOS";
  if (/Linux/i.test(platform)) return "Linux";
  return platform || "Unknown";
}

function isIos(nav = navigator) {
  return detectPlatform(nav) === "iOS";
}

function isStandalone(win = window, nav = navigator) {
  return Boolean(win?.matchMedia?.("(display-mode: standalone)")?.matches || nav?.standalone);
}

function getUserAgent() {
  return typeof navigator === "undefined" ? "" : String(navigator.userAgent || "").slice(0, 500);
}
